<template>
  <MasterLayout>

    <div class="flex flex-col">

      <div class="text-white mt-8 flex items-center font-bold text-4xl">
        <span class="mr-4 italic">{{ $t('account') }}</span>
      </div>

      <hr class="opacity-25 border-white my-4">

      <div class="text-white text-xl font-bold">
        <span class="">{{ $t('acc_phone') }} <i> +{{ user.phone }}</i></span>
      </div>

      <BaseInput v-model="name" class="mt-4">
        <template #label>
          {{ $t('acc_name_username') }}
        </template>
      </BaseInput>

      <div v-if="!nameAndUsernameIsValid" class="bg-danger-0 p-3 rounded-lg mt-1">
        <div class="text-center text-white  ">
          {{ $t('insert_valid_username')}}
        </div>
      </div>

      <div class="flex justify-end mt-4">
        <FloatButton @click="saveUser" :disabled="!nameAndUsernameIsValid" :loading="loading" :icon="['fas','check']">
          <span class="px-6 mr-4">{{ $t('save') }} </span>
        </FloatButton>
      </div>

      <hr class="opacity-25 border-white my-4">

      <div class="flex justify-end mt-4">
        <FloatButton :loading="loadingLogOut" @click="logout" :icon="['fas','door-open']">
          <span class="px-6 mr-4">{{ $t('logout') }} </span>
        </FloatButton>
      </div>

      <hr class="opacity-25 border-white my-4">

      <section class="border border-danger-0 rounded-2xl p-4 bg-primary-1 text-white">
        <div class="flex items-center text-danger-0 text-xl font-bold">
          <font-awesome-icon :icon="['fal', 'triangle-exclamation']" class="mr-3"/>
          <span>{{ $t('delete_account') }}</span>
        </div>
        <p class="mt-3 text-base leading-6">
          {{ $t('delete_account_description') }}
        </p>
        <div class="flex justify-end mt-4">
          <button
              type="button"
              class="border border-danger-0 text-danger-0 rounded-full px-5 py-3 font-bold disabled:opacity-50"
              :disabled="loadingDeleteAccount"
              @click="openDeleteConfirmation"
          >
            <font-awesome-icon :icon="['fal', 'trash-can']" class="mr-2"/>
            {{ $t('delete_account') }}
          </button>
        </div>
      </section>

    </div>

    <ModalWindow
        v-model="showDeleteConfirmation"
        :close-on-click-outside="!loadingDeleteAccount"
    >
      <template #title>
        <h2 class="text-xl font-bold text-primary-0 pr-4">
          {{ deleteConfirmationStep === 1
            ? $t('delete_account_confirmation_title')
            : $t('delete_account_final_title') }}
        </h2>
      </template>

      <template #content>
        <p class="text-primary-0 text-base leading-6 mt-4">
          {{ deleteConfirmationStep === 1
            ? $t('delete_account_confirmation_text')
            : $t('delete_account_final_text') }}
        </p>
      </template>

      <template #footer>
        <div class="flex flex-col gap-3 mt-5">
          <button
              v-if="deleteConfirmationStep === 1"
              type="button"
              class="bg-danger-0 text-white rounded-full px-5 py-3 font-bold"
              @click="deleteConfirmationStep = 2"
          >
            {{ $t('delete_account_continue') }}
          </button>
          <button
              v-else
              type="button"
              class="bg-danger-0 text-white rounded-full px-5 py-3 font-bold disabled:opacity-50"
              :disabled="loadingDeleteAccount"
              @click="deleteAccount"
          >
            <font-awesome-icon
                v-if="loadingDeleteAccount"
                :icon="['fad', 'spinner-third']"
                class="fa-spin mr-2"
            />
            {{ $t('delete_account_confirm') }}
          </button>
          <button
              type="button"
              class="text-primary-0 rounded-full px-5 py-3 font-bold disabled:opacity-50"
              :disabled="loadingDeleteAccount"
              @click="closeDeleteConfirmation"
          >
            {{ $t('cancel') }}
          </button>
        </div>
      </template>
    </ModalWindow>

  </MasterLayout>
</template>

<script setup>
import {ref, computed, watch} from "vue";
import {useRouter} from "vue-router";
import useSerStore from "@/stores/userStore";
import {ROUTE_NAMES} from "@/router";
import MasterLayout from "@/layouts/MasterLayout.vue";
import BaseInput from "@/components/inputs/BaseInput.vue";
import FloatButton from "@/components/buttons/FloatButton.vue";
import ModalWindow from "@/components/admin/ModalWindow.vue";
import Supabase from "@/plugins/Supabase";
import {
  checkPermissions,
  removeAllDeliveredNotifications,
  requestPermissions,
  deleteToken
} from "@/plugins/capacitor/Notifications";
import {useI18n} from "vue-i18n";

const {t} = useI18n()
const router = useRouter()
const userStore = useSerStore();
const user = userStore.getUser()

const name = ref(user?.user_metadata?.nameAndUsername ?? '')
const loading = ref(false)

const nameAndUsernameIsValid = computed(() => {
  if (name.value.length < 3 || !name.value.includes(' ')) {
    return false
  }

  return true
})

const saveUser = async () => {
  loading.value = true
  const {data: {user}, error} = await Supabase.auth.updateUser({
    data: {
      nameAndUsername: name.value.toString()
    }
  })
  loading.value = false

  console.log('saveUser()', {
    user,
    error
  })

  loading.value = false

  if (error) {
    alert(t('acc_error_save'))
    return
  }

  userStore.setUser(user)

}

const loadingLogOut = ref(false)
const logout = async () => {
  if (!confirm(t('sure_logout'))) return
  loadingLogOut.value = true

  try {
    await removeAllDeliveredNotifications()

    // const perm = checkPermissions()
    // if (perm) {
    //   await requestPermissions()
    //   const userToken = getToken()
    //   if (userToken) {
    //     const {
    //       data,
    //       error
    //     } = await Supabase.from('v2_Hajcer_App_Users_Fcm_Tokens').delete().eq('token', userToken).eq('userId', user.id)
    //     console.log('delete token ', {data, error})
    //   }
    // }
  } catch (e) {
    console.log('logout error token', e)
  }

  try {
    await deleteToken()
  } catch (e) {
    console.log('deleteToken error', e)
  }

  loadingLogOut.value = false
  await userStore.logout();
  await router.push({name: ROUTE_NAMES.HOME_VIEW})
}

const showDeleteConfirmation = ref(false)
const deleteConfirmationStep = ref(1)
const loadingDeleteAccount = ref(false)

watch(showDeleteConfirmation, (isOpen) => {
  if (!isOpen) deleteConfirmationStep.value = 1
})

const openDeleteConfirmation = () => {
  deleteConfirmationStep.value = 1
  showDeleteConfirmation.value = true
}

const closeDeleteConfirmation = () => {
  if (loadingDeleteAccount.value) return
  showDeleteConfirmation.value = false
}

const deleteAccount = async () => {
  if (loadingDeleteAccount.value || deleteConfirmationStep.value !== 2) return

  loadingDeleteAccount.value = true

  try {
    const {data: {session}, error: sessionError} = await Supabase.auth.getSession()
    if (sessionError || !session?.access_token) throw new Error('Missing authenticated session')

    const response = await fetch(`${import.meta.env.VITE_API_URL}/delete-account`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    })

    const result = await response.json().catch(() => null)
    if (!response.ok || !result?.success) throw new Error(result?.error ?? 'Account deletion failed')

    try {
      await removeAllDeliveredNotifications()
      await deleteToken()
    } catch (error) {
      console.warn('Push notification cleanup failed after account deletion', error)
    }

    await Supabase.removeAllChannels()
    await Supabase.auth.signOut({scope: 'local'}).catch(() => {})
    userStore.clearUser()
    showDeleteConfirmation.value = false
    alert(t('delete_account_success'))
    await router.replace({name: ROUTE_NAMES.HOME_VIEW})
  } catch (error) {
    console.error('deleteAccount()', error)
    alert(t('delete_account_error'))
  } finally {
    loadingDeleteAccount.value = false
  }
}

</script>
